/**
 * Created by rathompson on 10/25/21
 * File: IdentityInformation
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Bold,
  LoadingIcon,
  ModalBody,
  Title,
  noop,
} from '@panwds/react-ui'
import {
  LABEL_IDENTITY_INFORMATION,
  LABEL_IDENTITY_TYPE,
  ERROR_SERVER_ADD,
} from '../../../../../constants/LabelConstants'
import {
  Form,
  Select,
} from '@panwds/react-form'
import UserLayout from './formLayouts/UserLayout'
import ServiceAccountLayout from './formLayouts/ServiceAccountLayout'
import IAMService from '../../../../../services/IAMService'
import { IDENTITY_TYPES } from '../../../../../constants/IAMConstants'
import { DATA_TEST_ADD_IDENTITY_TYPE } from '../../../../../../common/constants/TestingDataIds'
import { checkPersonalEmail } from '../../../../../../common/utils/Utils'
import { getVarValue } from '../../../../../../common/utils/Sparky'
import { FF_ROLES_ENABLED } from '../../../../../../common/utils/FeatureFlags'

const ACCOUNT_TYPES = {
  user: {
    id: IDENTITY_TYPES.USER,
    value: 'User',
  },
  service_account: {
    id: IDENTITY_TYPES.SERVICE_ACCOUNT,
    value: 'Service Account',
  },
  group: {
    id: IDENTITY_TYPES.GROUP,
    value: 'User Group',
  },
}

const rolesEnabled = getVarValue(FF_ROLES_ENABLED, false)

/**
 *   Form for adding new members
 */
const IdentityInformationOld = ({
  tenant,
  closeModal,
  member,
  setMember,
  identityType,
  setIdentityType,
  onNextClicked,
  isEdit,
  loading: _loading,
  error: _error,
  onRemove,
  length,
}) => {
  /**
   *     ========= Local State ===========
   */
  const { members, serviceAccounts } = IAMService

  const [loading, setLoading] = useState(_loading)
  const [error, setError] = useState('')

  const items = useMemo(() => [ACCOUNT_TYPES.user, ACCOUNT_TYPES.service_account], [])
  const defaultItem = useMemo(() => ACCOUNT_TYPES[identityType] || ACCOUNT_TYPES.user, [identityType])

  /**
   *     ========= Lifecycle =============
   */
  useEffect(() => {
    setLoading(_loading)
  }, [_loading])

  useEffect(() => {
    if (_error && _error !== error) {
      setError(_error)
    }
  }, [_error, error])

  useEffect(() => {
    if (member?.identity_type === IDENTITY_TYPES.SERVICE_ACCOUNT && member?.identity_id && !member?.name) {
      setLoading(true)
      IAMService.serviceAccounts.get(tenant?.id, member?.identity_id)
        .then(response => {
          setMember({ ...member, ...response.data })
        })
        .catch(error => {
          setError(error.message)
        })
        .finally(() => {
          setLoading(false)
        })
    }
  }, [member, setMember, tenant?.id])

  /**
   *     ========= Callback ===========
   */
  const handleUserSubmit = useCallback(async (formData) => {
    const { identity_type, identity_email, display_name } = formData
    if (!rolesEnabled && (member || isEdit)) {
      onNextClicked()
    }
    else {
      checkPersonalEmail(identity_email)
      if (rolesEnabled) {
        setMember({ identity_type, identity_email, display_name })
        return { data: { identity_type, identity_email, display_name } }
      }
      else {
        return await members.post(tenant?.id, { identity_type: identity_type.id, identity_email, display_name })
      }
    }
  }, [isEdit, member, members, onNextClicked, setMember, tenant?.id])

  const handleServiceAccountSubmit = useCallback(async (formData) => {
    const { name, contact_email, description } = formData
    let res
    if (rolesEnabled && member && (!isEdit || !member.inherited_from.includes(tenant?.id))) {
      onNextClicked()
    }
    else if (!rolesEnabled && member && (!isEdit || member.inherited_from !== tenant?.id)) {
      onNextClicked()
    }
    if (contact_email) {
      checkPersonalEmail(contact_email)
    }
    if (isEdit) {
      if (!rolesEnabled && member?.inherited_from === tenant?.id) {
        res = await serviceAccounts.put(tenant?.id, member?.id, { contact_email, description })
      }
      else if (rolesEnabled && member?.tsg_id === tenant?.id) {
        if (
          (Boolean(contact_email) && contact_email !== member.contact_email) ||
          (Boolean(description) && description !== member.description)
        ) {
          res = await serviceAccounts.put(tenant?.id, member?.id, { contact_email, description })
        }
      }
    }
    else {
      if (!member) {
        res = await serviceAccounts.post(tenant?.id, { name, contact_email, description })
      }
    }
    if (res?.data) {
      res.data.identity_id = res?.data?.id
      res.data.identity_email = res?.data?.client_id
    }
    return Promise.resolve(res)
  }, [isEdit, member, onNextClicked, serviceAccounts, tenant?.id])

  const onSubmit = useCallback(async (formData) => {
    const { identity_type } = formData
    setLoading(true)
    setError('')
    try {
      let res
      switch (identity_type.id) {
        case IDENTITY_TYPES.USER:
          res = await handleUserSubmit(formData)
          break
        case IDENTITY_TYPES.SERVICE_ACCOUNT:
          res = await handleServiceAccountSubmit(formData)
          break
        default:
          res = null
      }
      if (res?.data) {
        setMember({ ...member, ...res?.data })
      }
      if (isEdit) {
        onNextClicked()
      }
    }
    catch (error) {
      setError(error.message || ERROR_SERVER_ADD)
    }
    setLoading(false)
  }, [isEdit, handleUserSubmit, handleServiceAccountSubmit, setMember, member, onNextClicked])

  const currentLayout = useMemo(() => {
    switch (identityType) {
      case IDENTITY_TYPES.SERVICE_ACCOUNT:
        return <ServiceAccountLayout onBackPressed={closeModal} {...{
          member,
          tenant,
          isEdit,
          onRemove,
          loading,
          length,
        }}/>
      case IDENTITY_TYPES.USER:
      default:
        return <UserLayout onBackPressed={closeModal} onNextPressed={onNextClicked} {...{
          member,
          tenant,
          isEdit,
          onRemove,
          loading,
          setError,
          length,
        }}/>
    }
  }, [identityType, closeModal, member, tenant, isEdit, onRemove, loading, onNextClicked, length])

  return (
    <Form {...{ onSubmit }} mode="onChange" formProps={{ style: { height: '100%', width: '100%' } }}>
      <div className="tw-flex tw-flex-row tw-h-full">
        {loading ? (
          <ModalBody addClassName="tw-mr-4 tw-flex tw-flex-row tw-justify-center tw-items-center tw-h-inherit">
            <LoadingIcon size="5xl"/>
          </ModalBody>
        ) : (
          <ModalBody addClassName="tw-overflow-y-scroll" style={{ height: 'calc(100% - 43px)', paddingRight: '146px' }}>
            <div className="tw-flex tw-flex-col tw-w-full tw-h-full">
              <Title level={1} size={'xs'} addClassName="tw-flex tw-truncate">{LABEL_IDENTITY_INFORMATION}</Title>
              {error && <Bold appearance="semibold" addClassName="tw-text-error">{error}</Bold>}
              <div className="tw-mt-2 tw-flex tw-flex-col tw-flex-grow">
                <Bold appearance="semibold">{LABEL_IDENTITY_TYPE}</Bold>
                <div className="type-container tw-mt-1">
                  <Select
                    addClassName='cmm-modal-select'
                    data-testid={DATA_TEST_ADD_IDENTITY_TYPE}
                    showLabel={false}
                    name="identity_type"
                    label={LABEL_IDENTITY_TYPE}
                    fitToItemWidth
                    defaultValue={defaultItem}
                    disabled={(!!member && identityType === IDENTITY_TYPES.SERVICE_ACCOUNT) || isEdit}
                    required={!member}
                    register={{
                      onChange: (change) => {
                        if (!isEdit) {
                          setMember(null)
                        }
                        setIdentityType(change.target.value.id)
                      },
                    }}
                    {...{ items }}
                  />
                </div>
                {currentLayout}
              </div>
            </div>
          </ModalBody>
        )}
      </div>
    </Form>
  )
}

IdentityInformationOld.defaultProps = {
  closeModal: noop,
  tenant: {},
  member: null,
  setMember: noop,
  setIdentityType: noop,
  onNextClicked: noop,
}

export default IdentityInformationOld
